import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { activeFilterIsStudentFilter } from './helpers';
import { useUser } from '../../hooks';
import AugmentationButton from '../AugmentationButton';
import FilterFeedback from '../FilterFeedback';
import { StudentFiltersEnum } from '../../constants';
import { CircularProgress, IconButton, Theme, Tooltip, styled } from '@material-ui/core';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppDispatch';
import {
  setActiveFilter,
  setRefresh,
  setSelectedStudent,
} from '../../slices/filters/entity/FiltersSlice';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from '../FontAwesomeLocal';

const StyledIconButton = styled(IconButton)<Theme>(({ theme }) => ({
  background: theme.palette.grey['200'],
  marginLeft: theme.spacing(1),
}));

const StudentFilters: FC = () => {
  // hooks
  const { t } = useTranslation();
  const { user } = useUser();
  const dispatch = useAppDispatch();

  // redux
  const enabledFilters = useAppSelector((state) => state.filters.enabledFilters);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const isOutdated = useAppSelector((state) => state.filters.isOutdated);
  const noResultsFound = useAppSelector((state) => state.filters.noResultsFound);
  const hideFilterButtons = useAppSelector((state) => state.filters.hideFilterButtons);
  const newResults = useAppSelector((state) => state.filters.newResults);
  const refresh = useAppSelector((state) => state.filters.refresh);
  const isFetching = useAppSelector((state) => state.filters.isFetching);
  const book = useAppSelector((state) => state.privatePageSettings.book);

  const toggleStudentFilter = (studentFilter: StudentFiltersEnum) => {
    if (studentFilter === activeFilter) {
      dispatch(setActiveFilter(undefined));
      dispatch(setSelectedStudent(''));
    } else {
      dispatch(setSelectedStudent(user.id));
      dispatch(setActiveFilter(studentFilter));
    }
  };

  const handleRefresh = useCallback(() => {
    dispatch(setRefresh(true));
  }, [dispatch]);

  useEffect(() => {
    if (refresh) dispatch(setRefresh(false));
  }, [refresh, dispatch]);

  return !!book && !book.isDefault && activeFilterIsStudentFilter(activeFilter) ? (
    <div className="hide-on-print">
      {!hideFilterButtons && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {isFetching && <CircularProgress size={16} />}
          {!!activeFilter &&
            activeFilter !== StudentFiltersEnum.PROGRESS_AND_SCORES &&
            !isFetching && (
              <Tooltip title={t('shared.refresh')} placement="bottom">
                <StyledIconButton onClick={handleRefresh} disabled={!activeFilter}>
                  <FontAwesomeSpriteIcon icon={SpriteIconsLight.REFRESH} />
                </StyledIconButton>
              </Tooltip>
            )}
          {enabledFilters.includes(StudentFiltersEnum.SCORES) && (
            <AugmentationButton
              icon={SpriteIconsLight.SCORES}
              tooltip={t('shared.scores')}
              onButtonClick={() => toggleStudentFilter(StudentFiltersEnum.SCORES)}
              primary={activeFilter === StudentFiltersEnum.SCORES}
            />
          )}
          {enabledFilters.includes(StudentFiltersEnum.PROGRESS) && (
            <AugmentationButton
              icon={SpriteIconsLight.PROGRESS}
              tooltip={t('shared.progress')}
              onButtonClick={() => toggleStudentFilter(StudentFiltersEnum.PROGRESS)}
              primary={activeFilter === StudentFiltersEnum.PROGRESS}
            />
          )}
          {enabledFilters.includes(StudentFiltersEnum.PROGRESS_AND_SCORES) && (
            <AugmentationButton
              icon={SpriteIconsLight.PROGRESS_AND_SCORES}
              tooltip={t('shared.progressAndScores')}
              onButtonClick={() => toggleStudentFilter(StudentFiltersEnum.PROGRESS_AND_SCORES)}
              primary={activeFilter === StudentFiltersEnum.PROGRESS_AND_SCORES}
            />
          )}
        </div>
      )}
      {!!activeFilter && (
        <FilterFeedback
          isOutdated={isOutdated}
          noResultsFound={noResultsFound}
          newResults={newResults}
        />
      )}
    </div>
  ) : null;
};

export default StudentFilters;
